import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { HollywoodCube } from "@/components/HollywoodCube";
import {
  Waveform, Detective, ShieldSlash, ShieldWarning, Rocket, Globe, SealCheck, Bell,
  ArrowRight, Check, X, Star, MagnifyingGlass, PaperPlaneTilt, UsersThree,
} from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1614079708030-576bb1031068?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const PILLARS = [
  [Detective, "AI Reputation Scanning", "Deep, evidence-driven audits of everything the web says about you — scored and explained."],
  [ShieldWarning, "Negative Suppression", "Outrank and bury negative results with authoritative, positive content that keeps working 24/7."],
  [ShieldSlash, "Content Removal & Takedowns", "Draft formal de-index, webmaster and legal removal requests for defamatory pages."],
  [Rocket, "24/7 AI Content & SEO", "An always-on engine that writes and publishes keyword-optimized articles about you."],
  [Globe, "Search-Engine Publishing", "Your content is pushed straight to Bing, Yandex & Google via IndexNow + sitemap."],
  [SealCheck, "Verified Public Profiles", "A cinematic, SEO-optimized profile that ranks for your name and controls the first impression."],
  [Bell, "Monitoring & Alerts", "We re-scan around the clock and alert you the moment a new negative appears."],
];

const STEPS = [
  ["01", "Scan", "We audit your entire online footprint and score your reputation with evidence."],
  ["02", "Rebuild", "AI builds a phased plan and writes positive, authoritative content in your voice."],
  ["03", "Publish & Suppress", "Content goes live across your domain and gets pushed to search engines to outrank negatives."],
  ["04", "Monitor", "Continuous monitoring + alerts keep your name protected long-term."],
];

const COMPARE = [
  ["Live in minutes, not weeks", true, false],
  ["AI content written & published for you", true, false],
  ["Instant search-engine submission (IndexNow)", true, false],
  ["Negative suppression + removal drafting", true, true],
  ["24/7 automated monitoring & alerts", true, false],
  ["Flat creator-friendly pricing", true, false],
  ["Built for artists, athletes & founders", true, false],
];

const TESTIMONIALS = [
  ["“SMI buried three old hit-pieces under my own story in weeks. My name finally looks like me.”", "Marcus R.", "Hip-Hop Artist"],
  ["“The 24/7 engine publishes while I sleep. My search results have never looked this clean.”", "Elena V.", "Startup Founder"],
  ["“Scan, rebuild, monitor — it's the whole reputation team in one dashboard.”", "Coach D.", "Author & Speaker"],
];

export default function Landing() {
  const [cats, setCats] = useState([]);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/branding-categories").then(({ data }) => setCats(data.categories)).catch(() => {});
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const registerHref = (cat) => `/login?mode=register${cat ? `&category=${encodeURIComponent(cat)}` : ""}`;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>SMI — AI Reputation & Influence Platform for Creators, Artists & Founders</title>
        <meta name="description" content="SMI is the AI reputation engine that rebuilds your image, suppresses negatives, and floods search with positive, verified content. Built for artists, athletes, influencers and entrepreneurs." />
        <meta property="og:title" content="SMI — Run the game. Own your name." />
        <meta property="og:description" content="AI reputation rebuild, negative suppression, removal, 24/7 SEO publishing and monitoring." />
        <link rel="canonical" href={`${window.location.origin}/`} />
      </Helmet>

      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-30 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Waveform size={26} weight="fill" className="text-primary" />
            <span className="font-head font-bold text-xl tracking-tighter gold-foil">SMI</span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#services" className="hover:text-white transition-colors duration-200">Services</a>
            <a href="#how" className="hover:text-white transition-colors duration-200">How it works</a>
            <a href="#categories" className="hover:text-white transition-colors duration-200">Who it's for</a>
            <a href="#pricing" className="hover:text-white transition-colors duration-200">Pricing</a>
            <Link to="/network" className="hover:text-white transition-colors duration-200">Network</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">Log in</Link>
            <Link to={registerHref()} data-testid="nav-register" className="bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm hover:bg-primary/90 transition-colors duration-200">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url('${HERO}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/85 to-[#080808]/50" />
        <div className="absolute inset-0 spotlight" />
        <div className="absolute top-24 right-8 hidden lg:block"><HollywoodCube size={120} /></div>
        <div className="relative max-w-6xl mx-auto px-6 pt-20 fade-up">
          <div className="overline text-primary mb-4">SOCIAL MEDIA INFLUENCER · AI REPUTATION ENGINE</div>
          <h1 className="font-head font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Run the game.<br /><span className="gold-foil">Own your name.</span>
          </h1>
          <p className="text-lg text-foreground/80 mt-6 max-w-2xl">Your reputation, rebuilt. Your influence, amplified. SMI scans the web, suppresses the negatives, and floods search with positive, verified content — 24/7.</p>
          <div className="flex flex-wrap items-center gap-4 mt-9">
            <Link to={registerHref()} data-testid="hero-register-btn"
              className="shimmer-cta bg-primary text-primary-foreground font-bold px-7 py-4 text-sm uppercase tracking-wider flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(233,172,12,0.45)] transition-[transform,box-shadow] duration-300">
              Start My Reputation Rebuild <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/login" className="border border-border px-7 py-4 text-sm font-semibold hover:border-primary hover:text-primary transition-colors duration-200">I already have an account</Link>
          </div>
          <div className="flex flex-wrap gap-8 mt-14 border-t border-border pt-8 max-w-2xl">
            {[["24/7", "Always-on engine"], ["8-score", "Reputation audit"], ["4+", "Search engines fed"]].map(([n, l]) => (
              <div key={l}><div className="font-head font-bold text-3xl gold-foil">{n}</div><div className="overline text-muted-foreground mt-1">{l}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-24">
        <div className="overline text-primary mb-3">THE FULL REPUTATION ARSENAL</div>
        <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-4">Everything a reputation firm does — automated.</h2>
        <p className="text-muted-foreground max-w-2xl mb-12">One platform replaces a whole agency: scanning, suppression, removals, content, SEO, profiles and monitoring.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map(([Icon, title, desc], i) => (
            <div key={i} className="border border-border bg-card p-6 hover:border-primary transition-colors duration-200 group" data-testid={`pillar-${i}`}>
              <Icon size={30} weight="fill" className="text-primary mb-4" />
              <h3 className="font-head font-bold text-lg mb-2 normal-case">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="overline text-primary mb-3">HOW IT WORKS</div>
          <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-12">Four moves to a clean name.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="border border-border p-6 bg-background">
                <div className="font-head font-bold text-4xl text-primary/40 mb-3">{n}</div>
                <h3 className="font-head font-bold text-xl mb-2 normal-case">{t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="max-w-6xl mx-auto px-6 py-24">
        <div className="overline text-primary mb-3">WHO IT'S FOR</div>
        <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-4">Pick your lane.</h2>
        <p className="text-muted-foreground max-w-2xl mb-12">SMI tailors your profile and AI content to your world. Choose a category to start — you can change it anytime.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cats.map((c, i) => (
            <Link key={c} to={registerHref(c)} data-testid={`category-card-${i}`}
              className="border border-border bg-card p-5 hover:border-primary hover:bg-primary/5 transition-colors duration-200 group flex items-center justify-between gap-2">
              <span className="font-head font-semibold text-sm normal-case">{c}</span>
              <ArrowRight size={16} weight="bold" className="text-muted-foreground group-hover:text-primary transition-colors duration-200 shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      {/* Why SMI comparison */}
      <section className="border-y border-border bg-card/40">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="overline text-primary mb-3">WHY SMI</div>
          <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-12">The agency alternative that never sleeps.</h2>
          <div className="border border-border overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] text-sm">
              <div className="bg-secondary/50 p-4 overline text-muted-foreground">Capability</div>
              <div className="bg-primary/10 p-4 overline text-primary text-center w-28">SMI</div>
              <div className="bg-secondary/50 p-4 overline text-muted-foreground text-center w-28">Typical firm</div>
              {COMPARE.map(([label, smi, firm], i) => (
                <div key={i} className="contents">
                  <div className="p-4 border-t border-border">{label}</div>
                  <div className="p-4 border-t border-border flex justify-center bg-primary/5">
                    {smi ? <Check size={20} weight="bold" className="text-emerald-400" /> : <X size={20} weight="bold" className="text-muted-foreground" />}
                  </div>
                  <div className="p-4 border-t border-border flex justify-center">
                    {firm ? <Check size={20} weight="bold" className="text-emerald-400" /> : <X size={20} weight="bold" className="text-muted-foreground" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="overline text-primary mb-3">FROM THE CULTURE</div>
        <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-12">Names, reclaimed.</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map(([quote, name, role], i) => (
            <div key={i} className="border border-border bg-card p-6" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4 text-primary">{[...Array(5)].map((_, j) => <Star key={j} size={14} weight="fill" />)}</div>
              <p className="text-sm leading-relaxed mb-5">{quote}</p>
              <div className="font-head font-bold text-sm">{name}</div>
              <div className="overline text-muted-foreground">{role}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-4">Illustrative examples of the SMI experience.</p>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="overline text-primary mb-3">PRICING</div>
          <h2 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter mb-12">Own your name for less than a night out.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p, i) => (
              <div key={p.id} className={`border p-7 bg-background relative ${p.popular ? "border-primary" : "border-border"}`} data-testid={`plan-${p.id}`}>
                {p.popular && <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 uppercase tracking-wider">Most popular</div>}
                <div className="font-head font-bold text-xl mb-1">{p.name}</div>
                <div className="font-head font-bold text-4xl gold-foil mb-1">${p.price}<span className="text-base text-muted-foreground font-body font-normal">/{p.interval}</span></div>
                <ul className="space-y-2 mt-5 mb-7">
                  {(p.features || []).map((f, j) => (
                    <li key={j} className="flex gap-2 text-sm text-muted-foreground"><Check size={16} weight="bold" className="text-primary shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link to={registerHref()} className={`block text-center font-semibold py-3 text-sm transition-colors duration-200 ${p.popular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border hover:border-primary hover:text-primary"}`}>Get {p.name}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="relative max-w-3xl mx-auto px-6 py-28 text-center">
          <UsersThree size={40} weight="fill" className="text-primary mx-auto mb-6" />
          <h2 className="font-head font-bold text-4xl sm:text-6xl tracking-tighter mb-5">Your name is your empire.<br /><span className="gold-foil">Protect it.</span></h2>
          <p className="text-muted-foreground mb-9 max-w-xl mx-auto">Join SMI and put an AI reputation team to work on your image today.</p>
          <Link to={registerHref()} data-testid="cta-register-btn"
            className="shimmer-cta inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-8 py-4 text-sm uppercase tracking-wider hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(233,172,12,0.45)] transition-[transform,box-shadow] duration-300">
            Register with SMI <PaperPlaneTilt size={18} weight="fill" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Waveform size={22} weight="fill" className="text-primary" />
            <span className="font-head font-bold tracking-tighter">SMI</span>
            <span className="overline text-muted-foreground ml-2">© {new Date().getFullYear()} Social Media Influencer</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/network" className="hover:text-white transition-colors duration-200">Network</Link>
            <Link to="/blog" className="hover:text-white transition-colors duration-200">Journal</Link>
            <Link to="/login" className="hover:text-white transition-colors duration-200">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
